import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, Check, X, Loader2, Calendar } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { Button } from '../ui/button';

/**
 * Banner shown on the producer dashboard listing all pending co-production invites.
 * Renders nothing when there are no pending invites. Accept/Reject inline.
 */
export default function CoProductionInvitesBanner() {
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actingOn, setActingOn] = useState(null);
  const navigate = useNavigate();

  const load = async () => {
    try {
      const r = await api.get('/co-production/invites');
      setInvites(Array.isArray(r.data) ? r.data : []);
    } catch (_) {
      // Silent — banner is just an extra
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const respond = async (eventId, accept) => {
    setActingOn(eventId);
    try {
      await api.post(`/co-production/invites/${eventId}/${accept ? 'accept' : 'reject'}`);
      toast.success(accept ? 'Invitación aceptada' : 'Invitación rechazada');
      if (accept) {
        // After accepting jump straight to the event manage page
        setTimeout(() => navigate(`/producer/events/${eventId}`), 600);
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo procesar');
    } finally {
      setActingOn(null);
    }
  };

  if (loading || invites.length === 0) return null;

  return (
    <div className="mb-6" data-testid="co-production-invites-banner">
      <AnimatePresence initial={false}>
        {invites.map((inv, idx) => (
          <motion.div
            key={inv.event_id}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, x: 40, height: 0, marginBottom: 0 }}
            transition={{ duration: 0.35, delay: idx * 0.04 }}
            className="relative overflow-hidden rounded-2xl border border-[#D4AF37]/35 mb-3"
            style={{
              background: 'linear-gradient(115deg, rgba(212,175,55,0.15), rgba(0,0,0,0.7) 70%)',
            }}
          >
            {/* glow */}
            <div className="absolute -top-20 -right-10 w-[320px] h-[320px] rounded-full blur-[120px] pointer-events-none" style={{ background: '#D4AF37', opacity: 0.13 }} />

            <div className="relative z-10 flex flex-col md:flex-row md:items-center gap-4 p-4 md:p-5">
              {/* Event image */}
              <div className="flex items-center gap-4 flex-1 min-w-0">
                {inv.event_image ? (
                  <img src={inv.event_image} alt={inv.event_title} className="w-14 h-14 rounded-xl object-cover border border-white/15" />
                ) : (
                  <div className="w-14 h-14 rounded-xl bg-zinc-900 border border-white/15 flex items-center justify-center">
                    <Calendar size={20} className="text-zinc-500" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 mb-1">
                    <Sparkles size={11} className="text-[#D4AF37]" />
                    <span className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#D4AF37]">
                      Invitación a co-producir
                    </span>
                  </div>
                  <p className="text-white font-bold truncate">{inv.event_title}</p>
                  <p className="text-[12px] text-white/60 truncate">
                    <strong className="text-white">{inv.primary?.name}</strong>{' '}
                    te invitó · <strong className="text-[#D4AF37]">{Number(inv.share_percent || 0).toFixed(1)}%</strong> del split
                  </p>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2 self-end md:self-auto">
                <Button
                  size="sm"
                  variant="outline"
                  className="border-zinc-700 text-zinc-300 hover:text-white"
                  onClick={() => respond(inv.event_id, false)}
                  disabled={actingOn === inv.event_id}
                  data-testid={`co-production-invite-reject-${inv.event_id}`}
                >
                  <X size={14} className="mr-1" /> Rechazar
                </Button>
                <Button
                  size="sm"
                  className="bg-[#D4AF37] hover:bg-[#F5D25A] text-black font-bold"
                  onClick={() => respond(inv.event_id, true)}
                  disabled={actingOn === inv.event_id}
                  data-testid={`co-production-invite-accept-${inv.event_id}`}
                >
                  {actingOn === inv.event_id ? <Loader2 size={14} className="animate-spin mr-1" /> : <Check size={14} className="mr-1" />}
                  Aceptar
                </Button>
              </div>
            </div>
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
}
